// ---------------------------------------------------------------------------
// SQLite schema (sql.js). Kept as plain DDL so it is portable to a Tauri SQL
// plugin / Drizzle migration later. JSON blobs are used for nested structures
// (structured profile, school fit, grading result, analytics) to keep the MVP
// schema readable while still storing everything described in the spec.
// ---------------------------------------------------------------------------

// v2: dental_program.interview_date, attempt_audio (recorded answers),
//     review_state (SM-2 spaced repetition), prompt-template refresh.
// v3: prompt templates refreshed to prompts-v1.2 (exact output contracts);
//     no table changes.
// v4: profile_document (uploaded primary application, stored for download).
// v5: research review workflow (interview_question.review_status/reviewed_at/
//     review_notes), mock resume (mock_interview.current_step), persisted
//     improvement_plan, saved_answer_version history, prompt_template_history,
//     and prompt templates refreshed to prompts-v1.3 (fit sources).
// v6: prompt templates refreshed to prompts-v1.4 (conversational grading,
//     conditional school fit, and post-2025 DAT scale guidance).
// v7: video practice (attempt_video + manual self-review), school dossiers
//     (program_dossier), red-flag coaching plans (red_flag_plan), and prompt
//     templates refreshed to prompts-v1.5 (research category focus + the new
//     dossier and redflag agents).
// v8: cloud sync metadata — sync_meta (device/account/cursor), sync_outbox
//     (change log populated by triggers), sync_doc_state (per-row server
//     revision), sync_conflict (local mirror of server conflicts), and
//     sync_control (trigger gate). Triggers on every syncable table record an
//     outbox entry so writes flow to the cloud without touching repository.ts.
// v9: sync_pending_blob — blob bytes whose metadata row hasn't arrived yet are
//     remembered (by server blob id) and downloaded once the meta doc lands,
//     so paged pulls can never permanently drop a recording/document.
export const SCHEMA_VERSION = 9;

const BASE_SCHEMA_SQL = `
PRAGMA foreign_keys = ON;

CREATE TABLE IF NOT EXISTS meta (
  key TEXT PRIMARY KEY,
  value TEXT NOT NULL
);

CREATE TABLE IF NOT EXISTS app_settings (
  id INTEGER PRIMARY KEY CHECK (id = 1),
  data TEXT NOT NULL
);

CREATE TABLE IF NOT EXISTS student_profile (
  id TEXT PRIMARY KEY,
  name TEXT NOT NULL,
  inputs TEXT NOT NULL,        -- JSON ProfileInputs
  structured TEXT,             -- JSON StructuredProfile | null
  created_at TEXT NOT NULL,
  updated_at TEXT NOT NULL
);

CREATE TABLE IF NOT EXISTS dental_program (
  id TEXT PRIMARY KEY,
  name TEXT NOT NULL,
  short_name TEXT NOT NULL,
  fit TEXT NOT NULL,           -- JSON SchoolFitProfile
  interview_date TEXT,         -- YYYY-MM-DD or NULL (added in v2)
  created_at TEXT NOT NULL
);

CREATE TABLE IF NOT EXISTS interview_question (
  id TEXT PRIMARY KEY,
  question TEXT NOT NULL,
  program_id TEXT,
  category TEXT NOT NULL,
  source TEXT NOT NULL DEFAULT '',
  source_url TEXT NOT NULL DEFAULT '',
  source_type TEXT NOT NULL DEFAULT 'manual',
  year TEXT NOT NULL DEFAULT '',
  tags TEXT NOT NULL DEFAULT '[]',     -- JSON string[]
  confidence TEXT NOT NULL DEFAULT 'medium',
  quality TEXT NOT NULL DEFAULT 'common',
  favorite INTEGER NOT NULL DEFAULT 0,
  practiced INTEGER NOT NULL DEFAULT 0,
  weak INTEGER NOT NULL DEFAULT 0,
  hidden INTEGER NOT NULL DEFAULT 0,
  notes TEXT NOT NULL DEFAULT '',
  review_status TEXT NOT NULL DEFAULT 'accepted',
  reviewed_at TEXT,
  review_notes TEXT NOT NULL DEFAULT '',
  created_at TEXT NOT NULL,
  FOREIGN KEY (program_id) REFERENCES dental_program (id) ON DELETE SET NULL
);

CREATE TABLE IF NOT EXISTS saved_answer (
  id TEXT PRIMARY KEY,
  question_id TEXT NOT NULL,
  generated TEXT,              -- JSON GeneratedAnswer | null
  final_answer TEXT NOT NULL DEFAULT '',
  is_best INTEGER NOT NULL DEFAULT 0,
  notes TEXT NOT NULL DEFAULT '',
  created_at TEXT NOT NULL,
  updated_at TEXT NOT NULL,
  FOREIGN KEY (question_id) REFERENCES interview_question (id) ON DELETE CASCADE
);

CREATE TABLE IF NOT EXISTS practice_session (
  id TEXT PRIMARY KEY,
  mode TEXT NOT NULL,
  created_at TEXT NOT NULL
);

CREATE TABLE IF NOT EXISTS mock_interview (
  id TEXT PRIMARY KEY,
  config TEXT NOT NULL,        -- JSON MockInterviewConfig
  plan TEXT NOT NULL,          -- JSON MockInterviewQuestionPlan[]
  attempt_ids TEXT NOT NULL DEFAULT '[]',
  report TEXT,                 -- JSON MockInterviewReport | null
  status TEXT NOT NULL DEFAULT 'planned',
  current_step INTEGER NOT NULL DEFAULT 0,
  created_at TEXT NOT NULL,
  completed_at TEXT
);

-- One row per graded answer. This is the table that gives repeatable grading:
-- normalized_hash is the SHA-256 cache key.
CREATE TABLE IF NOT EXISTS practice_attempt (
  id TEXT PRIMARY KEY,
  question_id TEXT NOT NULL,
  session_id TEXT,
  mock_interview_id TEXT,
  transcript_text TEXT NOT NULL,
  normalized_hash TEXT NOT NULL,
  rubric_version TEXT NOT NULL,
  prompt_version TEXT NOT NULL,
  model TEXT NOT NULL,
  scores TEXT NOT NULL,        -- JSON Record<string, number>
  final_score REAL NOT NULL,
  grading TEXT NOT NULL,       -- JSON GradingResult
  analytics TEXT NOT NULL,     -- JSON VoiceAnalytics
  created_at TEXT NOT NULL
);

CREATE INDEX IF NOT EXISTS idx_attempt_hash ON practice_attempt (normalized_hash);
CREATE INDEX IF NOT EXISTS idx_attempt_question ON practice_attempt (question_id);
CREATE INDEX IF NOT EXISTS idx_attempt_mock ON practice_attempt (mock_interview_id);
CREATE INDEX IF NOT EXISTS idx_question_program ON interview_question (program_id);
CREATE INDEX IF NOT EXISTS idx_question_category ON interview_question (category);

CREATE TABLE IF NOT EXISTS prompt_template (
  id TEXT PRIMARY KEY,
  agent TEXT NOT NULL,
  version TEXT NOT NULL,
  label TEXT NOT NULL,
  template TEXT NOT NULL,
  updated_at TEXT NOT NULL
);

-- Recorded audio for a graded attempt (delivery playback). One blob per attempt.
CREATE TABLE IF NOT EXISTS attempt_audio (
  attempt_id TEXT PRIMARY KEY,
  mime TEXT NOT NULL,
  data BLOB NOT NULL,
  created_at TEXT NOT NULL
);

-- Uploaded documents attached to a profile (e.g. the primary application PDF).
-- The original bytes are kept so the file can be re-downloaded; the extracted
-- text is kept for AI intake. One document per (profile, kind).
CREATE TABLE IF NOT EXISTS profile_document (
  profile_id TEXT NOT NULL,
  kind TEXT NOT NULL,          -- 'primary_application'
  filename TEXT NOT NULL,
  mime TEXT NOT NULL,
  data BLOB NOT NULL,
  text TEXT NOT NULL DEFAULT '',
  created_at TEXT NOT NULL,
  PRIMARY KEY (profile_id, kind)
);

-- Persisted improvement plans from Progress Analytics, with drill completion.
CREATE TABLE IF NOT EXISTS improvement_plan (
  id TEXT PRIMARY KEY,
  plan TEXT NOT NULL,                   -- JSON ImprovementPlanContent
  source_attempt_count INTEGER NOT NULL DEFAULT 0,
  baseline_average REAL NOT NULL DEFAULT 0,
  completed_drills TEXT NOT NULL DEFAULT '[]',  -- JSON number[]
  created_at TEXT NOT NULL
);

-- Version history of saved answers: one row per superseded final answer.
CREATE TABLE IF NOT EXISTS saved_answer_version (
  id TEXT PRIMARY KEY,
  answer_id TEXT NOT NULL,
  final_answer TEXT NOT NULL,
  saved_at TEXT NOT NULL,
  FOREIGN KEY (answer_id) REFERENCES saved_answer (id) ON DELETE CASCADE
);
CREATE INDEX IF NOT EXISTS idx_answer_version_answer ON saved_answer_version (answer_id);

-- Superseded prompt template bodies, kept so edits can be audited/restored.
CREATE TABLE IF NOT EXISTS prompt_template_history (
  id TEXT PRIMARY KEY,
  agent TEXT NOT NULL,
  version TEXT NOT NULL,
  template TEXT NOT NULL,
  saved_at TEXT NOT NULL
);

-- Recorded webcam video for a graded attempt (delivery playback), plus the
-- user's MANUAL self-review (eye contact / posture / habits) as JSON. There is
-- deliberately no automated body-language analysis.
CREATE TABLE IF NOT EXISTS attempt_video (
  attempt_id TEXT PRIMARY KEY,
  mime TEXT NOT NULL,
  data BLOB NOT NULL,
  self_review TEXT NOT NULL DEFAULT '',   -- JSON AttemptVideoReview | ''
  created_at TEXT NOT NULL
);

-- Deep per-school research dossier (JSON SchoolDossier), one per program.
CREATE TABLE IF NOT EXISTS program_dossier (
  program_id TEXT PRIMARY KEY,
  data TEXT NOT NULL,
  updated_at TEXT NOT NULL,
  FOREIGN KEY (program_id) REFERENCES dental_program (id) ON DELETE CASCADE
);

-- AI-personalized red-flag coaching (JSON RedFlagPlan), one per profile.
CREATE TABLE IF NOT EXISTS red_flag_plan (
  profile_id TEXT PRIMARY KEY,
  data TEXT NOT NULL,
  created_at TEXT NOT NULL
);

-- SM-2 spaced-repetition state per question, updated after every graded attempt.
CREATE TABLE IF NOT EXISTS review_state (
  question_id TEXT PRIMARY KEY,
  ease REAL NOT NULL DEFAULT 2.5,
  interval_days REAL NOT NULL DEFAULT 0,
  repetitions INTEGER NOT NULL DEFAULT 0,
  due_at TEXT NOT NULL,
  last_score REAL NOT NULL DEFAULT 0,
  updated_at TEXT NOT NULL
);
`;

// ---------------------------------------------------------------------------
// Cloud-sync metadata (schema v8). Kept separate from the domain schema above
// so it reads as the bolt-on it is. Nothing here runs until the user links a
// cloud account: the triggers are gated on sync_control.enabled = '1'.
// ---------------------------------------------------------------------------

/**
 * Every syncable client table and its primary-key expression. The trigger
 * generator turns this into an outbox entry on every insert/update/delete.
 * `kind: 'blob'` tables sync their bytes via the media endpoints; everything
 * else syncs as a JSON document.
 */
export interface SyncableTable {
  table: string;
  /** SQL expression for the entity id from a NEW/OLD row alias (defaults to the pk column). */
  pk: string;
  kind: 'doc' | 'blob';
}

export const SYNCABLE_TABLES: SyncableTable[] = [
  { table: 'app_settings', pk: 'id', kind: 'doc' },
  { table: 'student_profile', pk: 'id', kind: 'doc' },
  { table: 'dental_program', pk: 'id', kind: 'doc' },
  { table: 'interview_question', pk: 'id', kind: 'doc' },
  { table: 'saved_answer', pk: 'id', kind: 'doc' },
  { table: 'saved_answer_version', pk: 'id', kind: 'doc' },
  { table: 'practice_session', pk: 'id', kind: 'doc' },
  { table: 'practice_attempt', pk: 'id', kind: 'doc' },
  { table: 'mock_interview', pk: 'id', kind: 'doc' },
  { table: 'improvement_plan', pk: 'id', kind: 'doc' },
  { table: 'review_state', pk: 'question_id', kind: 'doc' },
  { table: 'program_dossier', pk: 'program_id', kind: 'doc' },
  { table: 'red_flag_plan', pk: 'profile_id', kind: 'doc' },
  { table: 'prompt_template', pk: 'id', kind: 'doc' },
  { table: 'attempt_audio', pk: 'attempt_id', kind: 'blob' },
  { table: 'attempt_video', pk: 'attempt_id', kind: 'blob' },
  { table: "profile_document", pk: "profile_id || '|' || kind", kind: 'blob' },
];

const SYNC_META_SQL = `
CREATE TABLE IF NOT EXISTS sync_meta (
  key TEXT PRIMARY KEY,
  value TEXT
);

CREATE TABLE IF NOT EXISTS sync_control (
  key TEXT PRIMARY KEY,
  value TEXT NOT NULL
);
INSERT OR IGNORE INTO sync_control (key, value) VALUES ('suppress', '0');
INSERT OR IGNORE INTO sync_control (key, value) VALUES ('enabled', '0');

-- Append-only change log. Populated by triggers; drained by the push step.
CREATE TABLE IF NOT EXISTS sync_outbox (
  seq INTEGER PRIMARY KEY AUTOINCREMENT,
  entity_type TEXT NOT NULL,
  entity_id TEXT NOT NULL,
  op TEXT NOT NULL,
  created_at TEXT NOT NULL DEFAULT (datetime('now'))
);
CREATE INDEX IF NOT EXISTS idx_outbox_entity ON sync_outbox (entity_type, entity_id);

-- Last server revision we know for each row, so pushes can send the correct
-- base revision and pulls can skip echoes / stale writes.
CREATE TABLE IF NOT EXISTS sync_doc_state (
  entity_type TEXT NOT NULL,
  entity_id TEXT NOT NULL,
  server_revision INTEGER NOT NULL DEFAULT 0,
  PRIMARY KEY (entity_type, entity_id)
);

-- Blob bytes we know exist on the server but could not apply yet because the
-- matching meta doc (which creates the local row) hasn't been pulled. Paged
-- pulls can deliver a blob change pages (or whole sync runs) before its meta
-- doc; entries here are retried after every pull until the row exists.
CREATE TABLE IF NOT EXISTS sync_pending_blob (
  entity_type TEXT NOT NULL,
  entity_id TEXT NOT NULL,
  blob_id TEXT NOT NULL,
  PRIMARY KEY (entity_type, entity_id)
);

-- Local mirror of unresolved server conflicts, for the conflict-resolution UI.
CREATE TABLE IF NOT EXISTS sync_conflict (
  id TEXT PRIMARY KEY,
  entity_type TEXT NOT NULL,
  entity_id TEXT NOT NULL,
  base_revision INTEGER NOT NULL,
  server_revision INTEGER NOT NULL,
  server_payload TEXT,
  client_payload TEXT,
  created_at TEXT NOT NULL,
  resolved INTEGER NOT NULL DEFAULT 0
);
`;

/** Build the three AFTER triggers (insert/update/delete) for one syncable table. */
function triggersFor({ table, pk }: SyncableTable): string {
  const gate =
    "(SELECT value FROM sync_control WHERE key='enabled')='1' AND " +
    "(SELECT value FROM sync_control WHERE key='suppress')='0'";
  const newId = pk.replace(/\b(id|kind|question_id|program_id|profile_id|attempt_id)\b/g, 'NEW.$1');
  const oldId = pk.replace(/\b(id|kind|question_id|program_id|profile_id|attempt_id)\b/g, 'OLD.$1');
  return `
CREATE TRIGGER IF NOT EXISTS trg_${table}_sync_ai AFTER INSERT ON ${table}
WHEN ${gate}
BEGIN INSERT INTO sync_outbox (entity_type, entity_id, op) VALUES ('${table}', ${newId}, 'upsert'); END;

CREATE TRIGGER IF NOT EXISTS trg_${table}_sync_au AFTER UPDATE ON ${table}
WHEN ${gate}
BEGIN INSERT INTO sync_outbox (entity_type, entity_id, op) VALUES ('${table}', ${newId}, 'upsert'); END;

CREATE TRIGGER IF NOT EXISTS trg_${table}_sync_ad AFTER DELETE ON ${table}
WHEN ${gate}
BEGIN INSERT INTO sync_outbox (entity_type, entity_id, op) VALUES ('${table}', ${oldId}, 'delete'); END;
`;
}

const SYNC_TRIGGERS_SQL = SYNCABLE_TABLES.map(triggersFor).join('\n');

export const SCHEMA_SQL = BASE_SCHEMA_SQL + SYNC_META_SQL + SYNC_TRIGGERS_SQL;
